import Axios, { AxiosInstance, AxiosRequestConfig, RawAxiosRequestHeaders, InternalAxiosRequestConfig, AxiosError } from 'axios'
import { RequestMethods, HttpResponse, HttpRequestConfig, HttpResponseData } from './types'
import { getToken } from '../global'
import { tokenManager } from './tokenManager'
// 导入AxiosCanceler类
import { AxiosCanceler } from './axiosCancel'

// 默认请求头
enum DefaultHeaders {
	'Accept' = 'application/json, text/plain, */*',
	'Content-Type' = 'application/json',
	'X-Requested-With' = 'XMLHttpRequest'
}

const KmoaHeaders = () => ({
	[process.env.VITE_APP_Header_Token as string]: getToken()
})

// 创建AxiosCanceler实例
const axiosCanceler = new AxiosCanceler()
export class Http {
	private defaultConfig: AxiosRequestConfig = {
		// 请求超时时间
		timeout: 60 * 60 * 1000
	}
	constructor(defaultConfig?: AxiosRequestConfig) {
		// 请求拦截
		this.httpInterceptorsRequest()
		// 响应拦截
		this.httpInterceptorsResponse()
		if (defaultConfig) {
			this.defaultConfig = defaultConfig
		}
	}

	/** 保存当前Axios实例对象 */
	private axiosInstance: AxiosInstance = Axios.create(this.defaultConfig)

	/** 请求拦截 */
	private httpInterceptorsRequest(): void {
		this.axiosInstance.interceptors.request.use(
			(config: HttpRequestConfig & InternalAxiosRequestConfig) => {
				// 只有当ignoreCancel为false或undefined时，才执行取消请求的操作
				if (!config.ignoreCancel) {
					axiosCanceler.addPending(config)
				}
				return config
			},
			error => {
				return Promise.reject(error)
			}
		)
	}

	/** 响应拦截 */
	private httpInterceptorsResponse(): void {
		const instance = this.axiosInstance
		instance.interceptors.response.use(
			(response: HttpResponse) => {
				// 请求完成后移除对应的pending请求
				const config = response.config as HttpRequestConfig & InternalAxiosRequestConfig
				if (!config.ignoreCancel) {
					axiosCanceler.removePending(config)
				}
				return new Promise(async (resolve, reject) => {
					// 如果是二进制数据（如 arraybuffer），直接返回响应
					if (
						response.config.responseType === 'arraybuffer' ||
						response.config.responseType === 'blob' ||
						response.headers['content-type'] === 'text/event-stream'
					) {
						resolve(response)
						return
					}
					const res = response.data
					if (res.code === 601) {
						const originalRequest = response.config as AxiosRequestConfig & { _retry?: boolean }
						// 如果是401错误且不是刷新token的请求，尝试刷新token
						if (!originalRequest._retry) {
							originalRequest._retry = true
							try {
								// 刷新token
								const newAccessToken = await tokenManager.refreshTokens()
								// 更新请求头中的token
								if (originalRequest.headers) {
									originalRequest.headers[process.env.VITE_APP_Header_Token as string] = newAccessToken
								}
								// 重试原始请求
								resolve(this.axiosInstance.request(originalRequest))
							} catch (refreshError) {
								// 刷新token失败，跳转到登录页
								tokenManager.clearTokens()
								window.location.href = process.env.VITE_APP_LOGIN_URL as string
								return Promise.reject(refreshError)
							}
						}
					}
					if (res.code === 200) {
						resolve(res)
					} else {
						reject(res)
					}
				})
			},
			async (error: AxiosError) => {
				if (error.config) {
					const config = error.config as HttpRequestConfig & InternalAxiosRequestConfig
					if (!config.ignoreCancel) {
						axiosCanceler.removePending(config)
					}
				}
				return Promise.reject(error)
			}
		)
	}

	/** 通用请求工具函数 */
	public request<T>(
		method: RequestMethods,
		url: string,
		HttpRequestConfig: HttpRequestConfig,
		exAxiosConfig: AxiosRequestConfig = {}
	): Promise<HttpResponseData<T> & HttpResponse<T>> {
		// 默认headers
		const headers: RawAxiosRequestHeaders = {}
		// DefaultHeaders
		Object.keys(DefaultHeaders).forEach((eachKey: string) => {
			if (!exAxiosConfig.headers?.[eachKey]) {
				headers[eachKey] = DefaultHeaders[eachKey as keyof typeof DefaultHeaders]
			}
		})
		const kmoaHeaders = KmoaHeaders()
		Object.keys(kmoaHeaders).forEach((eachKey: string) => {
			if (!exAxiosConfig.headers?.[eachKey]) {
				headers[eachKey] = kmoaHeaders[eachKey]
			}
		})
		exAxiosConfig.headers = Object.assign(headers, exAxiosConfig.headers)
		// 处理baseUrl
		exAxiosConfig.baseURL = exAxiosConfig.baseURL || import.meta.env.VITE_APP_BASE_API || ''

		const config = {
			...HttpRequestConfig,
			...exAxiosConfig,
			method,
			url,
			// url参数
			params: HttpRequestConfig.getParams,
			// 请求体
			data: HttpRequestConfig.params
		}

		return this.axiosInstance.request(config)
	}

	/** 单独抽离的post工具函数 */
	public post<T>(url: string, HttpRequestConfig: HttpRequestConfig, exAxiosConfig?: AxiosRequestConfig) {
		return this.request<T>('post', url, HttpRequestConfig, exAxiosConfig)
	}

	/** 单独抽离的get工具函数 */
	public get<T>(url: string, HttpRequestConfig: HttpRequestConfig, exAxiosConfig?: AxiosRequestConfig) {
		return this.request<T>('get', url, HttpRequestConfig, exAxiosConfig)
	}
}

export const http = new Http()
